import { useEffect, useState } from 'react';

import { Language } from '@nx-smartmonkey/shared/domain';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreateRegisterTokenProps } from '../../../application/SessionService';
import { EventTrackTypes } from '../../../constants/EventTracks';
import * as routing from '../../../constants/Routing';
import { useIsLogged } from '../../../hooks/session/useIsLogged';
import { useRetrieveActiveLanguage } from '../../../hooks/translations/useRetrieveActiveLanguage';
import { useCaptureEvents } from '../../../hooks/useCaptureEvents';
import { useShowSnackbarMessage } from '../../../hooks/useShowSnackbarMessage';
import { useUpdateVehicleLocation } from '../../app/hooks/useUpdateVehicleLocation';
import { AuthContentWrapper } from '../components/AuthContentWrapper';
import { useAuth } from '../hooks/useAuth';
import { useCreateInitialRoute } from '../hooks/useCreateInitialRoute';
import { useSessionRegister } from '../hooks/useSessionRegister';
import { RegisterLoader } from './RegisterLoader';
import { RegisterLocation } from './RegisterLocation';
import { RegisterOrganizationName } from './RegisterOrganizationName';
import { RegisterVehicleType } from './RegisterVehicleType';
import { RegisterWelcome } from './RegisterWelcome';
import { RegisterWorkSchedule } from './RegisterWorkSchedule';

type RegisterSteps =
  | `welcome`
  | `organizationName`
  | `workSchedule`
  | `vehicleType`
  | `registering_loader`
  | `location`;

export const RegisterSoloDrivers = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { capture } = useCaptureEvents();
  const { isLogged } = useIsLogged();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { setAuthScreen, registerData, setContextData: setRegisterData } = useAuth();
  const {
    success: registerSuccess,
    error: errorRegistering,
    isLoading: isLoadingRegister,
    run: register,
  } = useSessionRegister();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const {
    success: vehicleLocationUpdated,
    isLoading: updatingVehicleLocation,
    run: updateVehicleLocation,
  } = useUpdateVehicleLocation();
  const {
    run: createInitialRoute,
    data: initialRoute,
    success: initialRouteCreated,
    isLoading: creatingInitialRoute,
  } = useCreateInitialRoute();

  const [registerStep, setRegisterStep] = useState<RegisterSteps>(`welcome`);
  const [isFakeLoading, setIsFakeLoading] = useState(false);

  useEffect(() => {
    // https://drivers.routal.com/auth?referral_id=67e4031a4d0fbd9e06997298
    const referralIdParam = searchParams.get(`referral_id`);

    setRegisterData({
      language: activeLanguage ?? Language.getDefaultLanguage().value,
      referralId: (referralIdParam ?? ``).length > 0 ? referralIdParam! : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (registerSuccess) {
      capture({
        type: EventTrackTypes.UserLoggedIn,
        payload: {
          [`From`]: registerData.provider === `apple` ? `Apple` : `Google`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerSuccess]);

  useEffect(() => {
    if (vehicleLocationUpdated) {
      createInitialRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleLocationUpdated]);

  useEffect(() => {
    if (initialRouteCreated && initialRoute) {
      navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, initialRoute.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRouteCreated]);

  useEffect(() => {
    if (isLogged && !(isFakeLoading || isLoadingRegister)) {
      setRegisterStep(`location`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, isFakeLoading, isLoadingRegister]);

  useEffect(() => {
    if (errorRegistering && !isFakeLoading) {
      showSnackbarMessage({
        messageIntlId: `infoerror.drivers.register.error`,
      });
      onPreviousStep();
      setIsFakeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorRegistering, isFakeLoading]);

  const registerDriver = (registerData: CreateRegisterTokenProps) => {
    setRegisterStep(`registering_loader`);
    setIsFakeLoading(true);
    setTimeout(() => {
      setIsFakeLoading(false);
    }, 1500);
    register(registerData);
  };

  const getStepContent = () => {
    switch (registerStep) {
      case `welcome`:
        return (
          <RegisterWelcome
            phone={registerData.phone!}
            nextStep={() => {
              setRegisterStep(`organizationName`);
            }}
          />
        );
      case `organizationName`:
        return (
          <RegisterOrganizationName
            organizationName={registerData.organizationName ?? ``}
            nextStep={(orgName) => {
              setRegisterData({ ...registerData, organizationName: orgName });
              setRegisterStep(`workSchedule`);
            }}
          />
        );
      case `workSchedule`:
        return (
          <RegisterWorkSchedule
            workSchedule={registerData.workSchedule ?? [28800, 61200]}
            nextStep={(workSchedule) => {
              setRegisterData({ ...registerData, workSchedule });
              setRegisterStep(`vehicleType`);
            }}
          />
        );
      case `vehicleType`:
        return (
          <RegisterVehicleType
            nextStep={(vehicleType) => {
              registerDriver({
                providerToken: registerData.providerToken!,
                provider: registerData.provider!,
                accessToken: registerData.accessToken!,
                organizationName: registerData.organizationName!,
                workSchedule: registerData.workSchedule!,
                language: registerData.language,
                vehicleType: vehicleType!,
                referralId: registerData.referralId,
              });
            }}
          />
        );
      case `registering_loader`:
        return <RegisterLoader />;
      case `location`:
        return (
          <RegisterLocation
            disabled={updatingVehicleLocation || creatingInitialRoute}
            nextStep={(location) => {
              updateVehicleLocation({ location: { lat: location.lat!, lng: location.lng! } });
            }}
          />
        );
      default:
        return null;
    }
  };

  const onPreviousStep = () => {
    console.log(`Calling previous step`, registerStep);
    switch (registerStep) {
      case `welcome`:
        setAuthScreen(`login-welcome`);
        break;
      case `organizationName`:
        setRegisterStep(`welcome`);
        break;
      case `workSchedule`:
        setRegisterStep(`organizationName`);
        break;
      case `vehicleType`:
        setRegisterStep(`workSchedule`);
        break;
      case `registering_loader`:
        setRegisterStep(`vehicleType`);
        break;
      default:
        break;
    }
  };

  return (
    <AuthContentWrapper
      fullScreen
      onPreviousStep={![`registering_loader`, `location`].includes(registerStep) ? onPreviousStep : undefined}
    >
      {getStepContent()}
    </AuthContentWrapper>
  );
};
